/**
 * Statistics Data Service - Phase 1 Implementation
 * 
 * Processes Firebase round data into dashboard statistics
 * Handles all 6 widgets: Overall, Letters, Trends, Fingers, Weak Spots, Calendar
 */

import { 
  FirebaseUserRound, 
} from '@/types/firebase';
import { getUserRounds } from '@/services/roundsService';
import {
  DashboardStats,
  OverallStats,
  LetterPerformanceData,
  DailyProgressData,
  FingerPerformanceData,
  WeakSpotData,
  CalendarData,
  AchievementData,
  TimePeriod,
  CustomPeriod,
  FINGER_MAPPING,
  DEFAULT_THRESHOLDS
} from '@/types/statistics';

class StatisticsDataService {
  
  /**
   * Main method to fetch complete Phase 1 dashboard statistics
   */
  async getDashboardStats(
    uid: string, 
    period: TimePeriod, 
    customPeriod?: CustomPeriod
  ): Promise<DashboardStats> {
    console.log('📊 Fetching Phase 1 dashboard statistics for:', { uid, period });
    
    try {
      // Get filtered rounds based on time period
      const rounds = await this.getFilteredRounds(uid, period, customPeriod);
      
      if (rounds.length === 0) {
        console.log('📭 No rounds found for period, returning empty stats');
        return this.getEmptyStats();
      }
      
      console.log('📈 Processing', rounds.length, 'rounds for Phase 1 statistics');
      
      // Process all statistics in parallel for performance
      const [
        overall,
        letters,
        dailyProgress,
        fingerPerformance,
        weakSpots,
        calendarData,
        achievements
      ] = await Promise.all([
        this.calculateOverallStats(rounds, period),
        this.calculateLetterPerformance(rounds),
        this.calculateDailyProgress(rounds),
        this.calculateFingerPerformance(rounds),
        this.identifyWeakSpots(rounds),
        this.generateCalendarData(rounds),
        this.calculateAchievements(rounds)
      ]);
      
      console.log('✅ Phase 1 statistics processing complete');
      
      return {
        overall,
        letters,
        dailyProgress,
        fingerPerformance,
        weakSpots,
        calendarData,
        achievements
      };
    } catch (error) {
      console.error('❌ Error processing statistics:', error);
      throw error;
    }
  }
  
  /**
   * Get filtered rounds based on time period
   */
  private async getFilteredRounds(
    uid: string,
    period: TimePeriod,
    customPeriod?: CustomPeriod
  ): Promise<FirebaseUserRound[]> {
    
    const now = new Date();
    let startDate: Date;
    
    switch (period) {
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'custom':
        if (!customPeriod) throw new Error('Custom period required');
        startDate = customPeriod.startDate;
        break;
      case 'all':
      default:
        // Get all rounds
        const result = await getUserRounds(uid, { limit: 1000 });
        return result.success ? result.data || [] : [];
    }
    
    // Get all rounds and filter client-side (can be optimized with server-side filtering later)
    const result = await getUserRounds(uid, { limit: 1000 });
    const allRounds = result.success ? result.data || [] : [];
    
    return allRounds.filter(round => {
      const roundDate = new Date(round.timestamp.seconds * 1000);
      return roundDate >= startDate && roundDate <= now;
    });
  }
  
  /**
   * Widget 1: Calculate overall statistics with trend analysis
   */
  private async calculateOverallStats(
    rounds: FirebaseUserRound[],
    _period: TimePeriod
  ): Promise<OverallStats> {
    
    if (rounds.length === 0) {
      return {
        timeSpent: 0,
        totalKeystrokes: 0,
        accuracy: 0,
        avgSpeed: 0,
        wpm: 0,
        bestWord5: 0,
        trend: 'stable',
        sessionsCount: 0,
        improvementPercent: 0
      };
    }
    
    // Calculate totals
    const totalDuration = rounds.reduce((sum, round) => sum + round.duration, 0);
    const totalKeystrokes = rounds.reduce((sum, round) => sum + round.overallStats.totalAttempts, 0);
    const totalCorrect = rounds.reduce((sum, round) => sum + round.overallStats.correctLetters, 0);
    
    const avgAccuracy = totalKeystrokes > 0 ? (totalCorrect / totalKeystrokes) * 100 : 0;
    const avgSpeed = totalDuration > 0 ? totalCorrect / (totalDuration / 1000) : 0;
    const avgWPM = totalDuration > 0 ? (totalCorrect / 5) / (totalDuration / 60000) : 0;
    
    // Find best 5-letter word time across all rounds
    const bestWord5 = rounds.reduce((best, round) => {
      const word5Time = round.words.word5Fastest;
      if (word5Time > 0 && (best === 0 || word5Time < best)) {
        return word5Time;
      }
      return best;
    }, 0);
    
    // Calculate trend and improvement
    const { trend, improvementPercent } = this.calculateTrendAndImprovement(rounds, 'accuracy');
    
    return {
      timeSpent: totalDuration,
      totalKeystrokes,
      accuracy: Number(avgAccuracy.toFixed(1)),
      avgSpeed: Number(avgSpeed.toFixed(1)),
      wpm: Number(avgWPM.toFixed(1)),
      bestWord5,
      trend,
      sessionsCount: rounds.length,
      improvementPercent: Number(improvementPercent.toFixed(1))
    };
  }
  
  /**
   * Widget 2: Calculate letter-by-letter performance data
   */
  private async calculateLetterPerformance(
    rounds: FirebaseUserRound[]
  ): Promise<LetterPerformanceData[]> {
    
    const letterAggregates: { [letter: string]: {
      totalTaps: number;
      correctTaps: number;
      speedSum: number;
      speedCount: number;
      bestTime: number;
      recentRounds: { round: FirebaseUserRound; stats: any }[];
    }} = {};
    
    // Aggregate letter data from all rounds
    rounds.forEach(round => {
      Object.entries(round.letters).forEach(([letter, stats]) => {
        if (!letterAggregates[letter]) {
          letterAggregates[letter] = {
            totalTaps: 0,
            correctTaps: 0,
            speedSum: 0,
            speedCount: 0,
            bestTime: 0,
            recentRounds: []
          };
        }
        
        const agg = letterAggregates[letter];
        agg.totalTaps += stats.tapTotal;
        agg.correctTaps += stats.tapCorrect;
        
        // Accumulate speed data for averaging
        if (stats.tapFastest > 0) {
          agg.speedSum += stats.tapFastest;
          agg.speedCount++;
        }
        
        // Update best time
        if (stats.tapFastest > 0 && (agg.bestTime === 0 || stats.tapFastest < agg.bestTime)) {
          agg.bestTime = stats.tapFastest;
        }
        
        // Store recent rounds for trend analysis (keep only last 10)
        agg.recentRounds.push({ round, stats });
        if (agg.recentRounds.length > 10) {
          agg.recentRounds.shift();
        }
      });
    });
    
    // Convert to performance data with enhanced metrics
    const letterPerformance = Object.entries(letterAggregates).map(([letter, agg]) => {
      const accuracy = agg.totalTaps > 0 ? (agg.correctTaps / agg.totalTaps) * 100 : 0;
      const avgSpeed = agg.speedCount > 0 ? agg.speedSum / agg.speedCount : 0;
      
      // Calculate recent accuracy for trend (last 5 rounds)
      const recentData = agg.recentRounds.slice(-5);
      const recentAccuracy = this.calculateLetterRecentAccuracy(letter, recentData);
      
      // Determine trend
      const trend: 'improving' | 'declining' | 'stable' = 
        recentAccuracy > accuracy + 3 ? 'improving' :
        recentAccuracy < accuracy - 3 ? 'declining' : 'stable';
      
      // Find finger assignment
      const fingerAssignment = this.getFingerForLetter(letter);
      
      // Calculate difficulty score (based on finger strength and letter complexity)
      const difficultyScore = this.calculateLetterDifficulty(letter, accuracy, avgSpeed);
      
      return {
        letter,
        practiceCount: agg.totalTaps,
        accuracy: Number(accuracy.toFixed(1)),
        avgSpeed: Number(avgSpeed.toFixed(0)),
        bestTime: agg.bestTime,
        trend,
        recentAccuracy: Number(recentAccuracy.toFixed(1)),
        fingerAssignment,
        difficultyScore
      };
    });
    
    // Sort by practice count (most practiced first)
    return letterPerformance.sort((a, b) => b.practiceCount - a.practiceCount);
  }
  
  /**
   * Widget 3: Calculate daily progress data
   */
  private async calculateDailyProgress(
    rounds: FirebaseUserRound[]
  ): Promise<DailyProgressData[]> {
    
    const dailyAggregates: { [date: string]: {
      rounds: FirebaseUserRound[];
      totalKeystrokes: number;
      totalCorrect: number;
      totalDuration: number;
    }} = {};
    
    // Group rounds by date
    rounds.forEach(round => {
      const date = new Date(round.timestamp.seconds * 1000).toISOString().split('T')[0];
      
      if (!dailyAggregates[date]) {
        dailyAggregates[date] = {
          rounds: [],
          totalKeystrokes: 0,
          totalCorrect: 0,
          totalDuration: 0
        };
      }
      
      const daily = dailyAggregates[date];
      daily.rounds.push(round);
      daily.totalKeystrokes += round.overallStats.totalAttempts;
      daily.totalCorrect += round.overallStats.correctLetters;
      daily.totalDuration += round.duration;
    });
    
    // Convert to daily progress data with quality scoring
    const dailyProgress = Object.entries(dailyAggregates)
      .map(([date, daily]) => {
        const accuracy = daily.totalKeystrokes > 0 ? 
          (daily.totalCorrect / daily.totalKeystrokes) * 100 : 0;
        const speed = daily.totalDuration > 0 ? 
          daily.totalCorrect / (daily.totalDuration / 1000) : 0;
        const wpm = daily.totalDuration > 0 ? 
          (daily.totalCorrect / 5) / (daily.totalDuration / 60000) : 0;
        
        // Calculate session quality score (1-10)
        const qualityScore = this.calculateSessionQuality(accuracy, speed, daily.rounds.length);
        
        return {
          date,
          accuracy: Number(accuracy.toFixed(1)),
          speed: Number(speed.toFixed(1)),
          wpm: Number(wpm.toFixed(1)),
          sessionsCount: daily.rounds.length,
          totalKeystrokes: daily.totalKeystrokes,
          totalDuration: daily.totalDuration,
          averageSessionQuality: Number(qualityScore.toFixed(1))
        };
      })
      .sort((a, b) => a.date.localeCompare(b.date)); // Sort chronologically
    
    return dailyProgress;
  }
  
  /**
   * Widget 4: Calculate finger performance analysis
   */
  private async calculateFingerPerformance(
    rounds: FirebaseUserRound[]
  ): Promise<FingerPerformanceData[]> {
    
    const fingerStats: { [finger: string]: {
      totalTaps: number;
      correctTaps: number;
      speedSum: number;
      speedCount: number;
      letters: string[];
      recentPerformance: number[];
    }} = {};
    
    // Initialize finger stats
    Object.entries(FINGER_MAPPING).forEach(([finger, config]) => {
      fingerStats[finger] = {
        totalTaps: 0,
        correctTaps: 0,
        speedSum: 0,
        speedCount: 0,
        letters: config.letters,
        recentPerformance: []
      };
    });
    
    // Aggregate letter stats to finger stats
    rounds.forEach(round => {
      Object.entries(round.letters).forEach(([letter, stats]) => {
        const finger = this.getFingerForLetter(letter);
        
        if (finger && fingerStats[finger]) {
          fingerStats[finger].totalTaps += stats.tapTotal;
          fingerStats[finger].correctTaps += stats.tapCorrect;
          
          if (stats.tapFastest > 0) {
            fingerStats[finger].speedSum += stats.tapFastest;
            fingerStats[finger].speedCount++;
          }
          
          // Track recent performance for improvement rate
          const letterAccuracy = stats.tapTotal > 0 ? (stats.tapCorrect / stats.tapTotal) * 100 : 0;
          fingerStats[finger].recentPerformance.push(letterAccuracy);
        }
      });
    });
    
    // Convert to finger performance data
    return Object.entries(fingerStats).map(([finger, stats]) => {
      const accuracy = stats.totalTaps > 0 ? (stats.correctTaps / stats.totalTaps) * 100 : 0;
      const avgSpeed = stats.speedCount > 0 ? stats.speedSum / stats.speedCount : 0;
      const config = FINGER_MAPPING[finger];
      
      // Calculate strength score relative to finger capability
      const strengthScore = this.calculateFingerStrength(accuracy, avgSpeed, config.strength);
      
      // Calculate improvement rate from recent performance
      const improvementRate = this.calculateImprovementRate(stats.recentPerformance);
      
      return {
        finger: finger.replace('left', '').replace('right', ''),
        hand: config.hand,
        accuracy: Number(accuracy.toFixed(1)),
        avgSpeed: Number(avgSpeed.toFixed(0)),
        assignedLetters: config.letters,
        practiceCount: stats.totalTaps,
        strengthScore: Number(strengthScore.toFixed(1)),
        improvementRate: Number(improvementRate.toFixed(1))
      };
    }).filter(finger => finger.practiceCount > 0); // Only include fingers with practice data
  }
  
  /**
   * Widget 5: Identify weak spots and improvement opportunities
   */
  private async identifyWeakSpots(
    rounds: FirebaseUserRound[]
  ): Promise<WeakSpotData[]> {
    
    const letterPerformance = await this.calculateLetterPerformance(rounds);
    
    // Identify letters that need improvement
    const weakSpots = letterPerformance
      .filter(letter => {
        // Multiple criteria for weakness
        return letter.accuracy < DEFAULT_THRESHOLDS.accuracy.good || 
               letter.avgSpeed > DEFAULT_THRESHOLDS.speed.slow ||
               letter.trend === 'declining';
      })
      .map(letter => {
        const priority = this.calculatePriority(letter);
        const improvementPotential = this.calculateImprovementPotential(letter);
        const recommendation = this.generateRecommendation(letter);
        const estimatedPracticeTime = this.estimatePracticeTime(letter);
        
        return {
          letter: letter.letter,
          accuracy: letter.accuracy,
          avgSpeed: letter.avgSpeed,
          practiceCount: letter.practiceCount,
          improvementPotential,
          recommendation,
          priority,
          estimatedPracticeTime
        };
      })
      .sort((a, b) => {
        // Sort by priority (high first) then by improvement potential
        if (a.priority !== b.priority) {
          const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        }
        return b.improvementPotential - a.improvementPotential;
      })
      .slice(0, 8); // Top 8 weak spots
    
    return weakSpots;
  }
  
  /**
   * Widget 6: Generate training calendar heatmap data
   */
  private async generateCalendarData(
    rounds: FirebaseUserRound[]
  ): Promise<CalendarData[]> {
    
    const dailyData: { [date: string]: {
      rounds: FirebaseUserRound[];
      totalDuration: number;
      accuracy: number;
      keystrokes: number;
    }} = {};
    
    // Group by date
    rounds.forEach(round => {
      const date = new Date(round.timestamp.seconds * 1000).toISOString().split('T')[0];
      
      if (!dailyData[date]) {
        dailyData[date] = {
          rounds: [],
          totalDuration: 0,
          accuracy: 0,
          keystrokes: 0
        };
      }
      
      dailyData[date].rounds.push(round);
      dailyData[date].totalDuration += round.duration;
      dailyData[date].keystrokes += round.overallStats.totalAttempts;
    });
    
    // Calculate intensity and quality for each day
    const calendarData: CalendarData[] = [];
    
    // Fill in the last 365 days (or available data)
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - 365 * 24 * 60 * 60 * 1000);
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      const dayData = dailyData[dateStr];
      
      if (dayData) {
        // Calculate average accuracy for the day
        const totalCorrect = dayData.rounds.reduce((sum, r) => sum + r.overallStats.correctLetters, 0);
        const totalAttempts = dayData.rounds.reduce((sum, r) => sum + r.overallStats.totalAttempts, 0);
        const accuracy = totalAttempts > 0 ? (totalCorrect / totalAttempts) * 100 : 0;
        
        // Calculate intensity (0-4 scale based on duration and sessions)
        const intensity = this.calculateIntensity(dayData.totalDuration, dayData.rounds.length);
        
        // Calculate quality score
        const qualityScore = this.calculateSessionQuality(accuracy, 0, dayData.rounds.length);
        
        calendarData.push({
          date: dateStr,
          intensity,
          sessionsCount: dayData.rounds.length,
          totalDuration: dayData.totalDuration,
          accuracy: Number(accuracy.toFixed(1)),
          qualityScore: Number(qualityScore.toFixed(1)),
          streak: false // Will be calculated separately
        });
      } else {
        // No practice on this day
        calendarData.push({
          date: dateStr,
          intensity: 0,
          sessionsCount: 0,
          totalDuration: 0,
          accuracy: 0,
          qualityScore: 0,
          streak: false
        });
      }
    }
    
    // Calculate streaks
    this.calculateStreaks(calendarData);
    
    return calendarData.sort((a, b) => a.date.localeCompare(b.date));
  }
  
  /**
   * Calculate achievements and milestones
   */
  private async calculateAchievements(
    rounds: FirebaseUserRound[]
  ): Promise<AchievementData[]> {
    
    const overall = await this.calculateOverallStats(rounds, 'all');
    const dailyProgress = await this.calculateDailyProgress(rounds);
    
    // Calculate streak data
    const currentStreak = this.calculateCurrentStreak(dailyProgress);
    // const longestStreak = this.calculateLongestStreak(dailyProgress); // Commented out as unused
    
    const achievements: AchievementData[] = [
      // Speed achievements
      {
        id: 'speed_20',
        title: '20 WPM Novice',
        description: 'Achieve 20 words per minute',
        achieved: overall.wpm >= 20,
        progress: Math.min(100, (overall.wpm / 20) * 100),
        category: 'speed',
        icon: '🐌'
      },
      {
        id: 'speed_30',
        title: '30 WPM Intermediate',
        description: 'Achieve 30 words per minute',
        achieved: overall.wpm >= 30,
        progress: Math.min(100, (overall.wpm / 30) * 100),
        category: 'speed',
        icon: '🚗'
      },
      {
        id: 'speed_40',
        title: '40 WPM Advanced',
        description: 'Achieve 40 words per minute',
        achieved: overall.wpm >= 40,
        progress: Math.min(100, (overall.wpm / 40) * 100),
        category: 'speed',
        icon: '🏎️'
      },
      {
        id: 'speed_50',
        title: '50 WPM Expert',
        description: 'Achieve 50 words per minute',
        achieved: overall.wpm >= 50,
        progress: Math.min(100, (overall.wpm / 50) * 100),
        category: 'speed',
        icon: '🚀'
      },
      
      // Accuracy achievements
      {
        id: 'accuracy_90',
        title: 'Steady Typist',
        description: 'Achieve 90% accuracy',
        achieved: overall.accuracy >= 90,
        progress: Math.min(100, (overall.accuracy / 90) * 100),
        category: 'accuracy',
        icon: '🎯'
      },
      {
        id: 'accuracy_95',
        title: 'Precision Typist',
        description: 'Achieve 95% accuracy',
        achieved: overall.accuracy >= 95,
        progress: Math.min(100, (overall.accuracy / 95) * 100),
        category: 'accuracy',
        icon: '🏹'
      },
      {
        id: 'accuracy_98',
        title: 'Near Perfect',
        description: 'Achieve 98% accuracy',
        achieved: overall.accuracy >= 98,
        progress: Math.min(100, (overall.accuracy / 98) * 100),
        category: 'accuracy',
        icon: '💎'
      },
      
      // Consistency achievements
      {
        id: 'streak_7',
        title: 'Week Warrior',
        description: 'Practice for 7 days in a row',
        achieved: currentStreak >= 7,
        progress: Math.min(100, (currentStreak / 7) * 100),
        category: 'consistency',
        icon: '📅'
      },
      {
        id: 'streak_30',
        title: 'Month Master',
        description: 'Practice for 30 days in a row',
        achieved: currentStreak >= 30,
        progress: Math.min(100, (currentStreak / 30) * 100),
        category: 'consistency',
        icon: '📈'
      },
      
      // Milestone achievements
      {
        id: 'sessions_50',
        title: 'Half Century',
        description: 'Complete 50 practice sessions',
        achieved: overall.sessionsCount >= 50,
        progress: Math.min(100, (overall.sessionsCount / 50) * 100),
        category: 'milestone',
        icon: '🥉'
      },
      {
        id: 'sessions_100',
        title: 'Century Club',
        description: 'Complete 100 practice sessions',
        achieved: overall.sessionsCount >= 100,
        progress: Math.min(100, (overall.sessionsCount / 100) * 100),
        category: 'milestone',
        icon: '🥈'
      },
      {
        id: 'sessions_500',
        title: 'Elite Practitioner',
        description: 'Complete 500 practice sessions',
        achieved: overall.sessionsCount >= 500,
        progress: Math.min(100, (overall.sessionsCount / 500) * 100),
        category: 'milestone',
        icon: '🥇'
      }
    ];
    
    return achievements;
  }
  
  // ===================
  // HELPER METHODS
  // ===================
  
  private calculateTrendAndImprovement(
    rounds: FirebaseUserRound[], 
    metric: 'accuracy' | 'speed'
  ): { trend: 'up' | 'down' | 'stable'; improvementPercent: number } {
    
    if (rounds.length < 4) {
      return { trend: 'stable', improvementPercent: 0 };
    }
    
    const midpoint = Math.floor(rounds.length / 2);
    const firstHalf = rounds.slice(0, midpoint);
    const secondHalf = rounds.slice(midpoint);
    
    const getAverage = (roundsSubset: FirebaseUserRound[]) => {
      if (metric === 'accuracy') {
        const total = roundsSubset.reduce((sum, r) => sum + r.overallStats.totalAttempts, 0);
        const correct = roundsSubset.reduce((sum, r) => sum + r.overallStats.correctLetters, 0);
        return total > 0 ? (correct / total) * 100 : 0;
      } else {
        const totalDuration = roundsSubset.reduce((sum, r) => sum + r.duration, 0);
        const totalCorrect = roundsSubset.reduce((sum, r) => sum + r.overallStats.correctLetters, 0);
        return totalDuration > 0 ? totalCorrect / (totalDuration / 1000) : 0;
      }
    };
    
    const firstAvg = getAverage(firstHalf);
    const secondAvg = getAverage(secondHalf);
    
    const improvementPercent = firstAvg > 0 ? ((secondAvg - firstAvg) / firstAvg) * 100 : 0;
    
    const trend: 'up' | 'down' | 'stable' = 
      improvementPercent > 5 ? 'up' :
      improvementPercent < -5 ? 'down' : 'stable';
    
    return { trend, improvementPercent };
  }
  
  private calculateLetterRecentAccuracy(
    _letter: string, 
    recentData: { round: FirebaseUserRound; stats: any }[]
  ): number {
    
    let totalTaps = 0;
    let correctTaps = 0;
    
    recentData.forEach(({ stats }) => {
      totalTaps += stats.tapTotal;
      correctTaps += stats.tapCorrect;
    });
    
    return totalTaps > 0 ? (correctTaps / totalTaps) * 100 : 0;
  }
  
  private getFingerForLetter(letter: string): string {
    const upperLetter = letter.toUpperCase();
    
    for (const [finger, config] of Object.entries(FINGER_MAPPING)) {
      if (config.letters.includes(upperLetter)) {
        return finger;
      }
    }
    
    return 'unknown';
  }
  
  private calculateLetterDifficulty(letter: string, accuracy: number, avgSpeed: number): number {
    const finger = this.getFingerForLetter(letter);
    const fingerStrength = FINGER_MAPPING[finger]?.strength || 0.5;
    
    // Difficulty based on finger strength, accuracy, and speed
    const difficultyFromFinger = (1 - fingerStrength) * 3; // 0-3 points
    const difficultyFromAccuracy = accuracy < 90 ? (90 - accuracy) / 10 : 0; // 0-9 points  
    const difficultyFromSpeed = avgSpeed > 200 ? Math.min((avgSpeed - 200) / 50, 5) : 0; // 0-5 points
    
    return Math.min(10, Math.round(difficultyFromFinger + difficultyFromAccuracy + difficultyFromSpeed));
  }
  
  private calculateSessionQuality(accuracy: number, _speed: number, sessionCount: number): number {
    // Quality score from 1-10 based on multiple factors
    let score = 5; // baseline
    
    // Accuracy contribution (0-4 points)
    if (accuracy >= 98) score += 4;
    else if (accuracy >= 95) score += 3;
    else if (accuracy >= 90) score += 2;
    else if (accuracy >= 85) score += 1;
    else score -= 1;
    
    // Consistency contribution (sessions per day)
    if (sessionCount >= 3) score += 1;
    else if (sessionCount >= 2) score += 0.5;
    
    return Math.max(1, Math.min(10, score));
  }
  
  private calculateFingerStrength(accuracy: number, avgSpeed: number, fingerCapacity: number): number {
    // Strength relative to finger's natural capability
    const accuracyScore = (accuracy / 100) * 5; // 0-5 points
    const speedScore = avgSpeed > 0 ? Math.max(0, 5 - (avgSpeed - 150) / 50) : 0; // 0-5 points
    const capacityAdjustment = fingerCapacity; // Adjust for finger strength
    
    return Math.max(0, Math.min(10, (accuracyScore + speedScore) * capacityAdjustment));
  }
  
  private calculateImprovementRate(performanceHistory: number[]): number {
    if (performanceHistory.length < 3) return 0;
    
    // Simple linear regression to find improvement trend
    const n = performanceHistory.length;
    const x = Array.from({ length: n }, (_, i) => i);
    const y = performanceHistory;
    
    const sumX = x.reduce((a, b) => a + b, 0);
    const sumY = y.reduce((a, b) => a + b, 0);
    const sumXY = x.reduce((acc, xi, i) => acc + xi * y[i], 0);
    const sumXX = x.reduce((acc, xi) => acc + xi * xi, 0);
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    
    // Convert slope to percentage improvement
    return slope * 100;
  }
  
  private calculatePriority(letter: LetterPerformanceData): 'high' | 'medium' | 'low' {
    if (letter.accuracy < 80 || letter.trend === 'declining') return 'high';
    if (letter.accuracy < 90 || letter.avgSpeed > 300) return 'medium';
    return 'low';
  }
  
  private calculateImprovementPotential(letter: LetterPerformanceData): number {
    // Score 0-100 based on room for improvement and practice frequency
    const accuracyGap = Math.max(0, 100 - letter.accuracy);
    const speedGap = letter.avgSpeed > 200 ? Math.min((letter.avgSpeed - 200) / 5, 20) : 0;
    const practiceBonus = Math.min(letter.practiceCount / 50, 20); // More practice = easier to improve
    
    return Math.round(accuracyGap * 0.6 + speedGap * 0.3 + practiceBonus * 0.1);
  }
  
  private generateRecommendation(letter: LetterPerformanceData): string {
    if (letter.accuracy < 75) {
      return `Focus on accuracy with ${letter.letter}. Practice slowly and build muscle memory.`;
    } else if (letter.accuracy < 90) {
      return `Improve ${letter.letter} accuracy through consistent, deliberate practice.`;
    } else if (letter.avgSpeed > 400) {
      return `Work on speed with ${letter.letter}. Use rhythm exercises and gradually increase pace.`;
    } else if (letter.trend === 'declining') {
      return `${letter.letter} performance is declining. Review finger positioning and take breaks.`;
    } else {
      return `Good progress with ${letter.letter}! Continue regular practice to maintain improvement.`;
    }
  }
  
  private estimatePracticeTime(letter: LetterPerformanceData): number {
    // Estimate minutes needed for meaningful improvement
    const baseTime = 10; // minimum practice time
    const accuracyFactor = letter.accuracy < 90 ? (90 - letter.accuracy) * 2 : 0;
    const speedFactor = letter.avgSpeed > 250 ? Math.min((letter.avgSpeed - 250) / 20, 15) : 0;
    
    return Math.round(baseTime + accuracyFactor + speedFactor);
  }
  
  private calculateIntensity(duration: number, sessionCount: number): number {
    // 0-4 intensity scale for heatmap
    const durationMinutes = duration / 60000;
    
    if (durationMinutes >= 60 || sessionCount >= 5) return 4; // Very high
    if (durationMinutes >= 30 || sessionCount >= 3) return 3; // High
    if (durationMinutes >= 15 || sessionCount >= 2) return 2; // Medium
    if (durationMinutes >= 5 || sessionCount >= 1) return 1; // Low
    return 0; // None
  }
  
  private calculateStreaks(calendarData: CalendarData[]): void {
    // Calculate which days are part of current streak
    let currentStreakLength = 0;
    
    // Go backwards from today to find current streak
    for (let i = calendarData.length - 1; i >= 0; i--) {
      const day = calendarData[i];
      if (day.sessionsCount > 0) {
        currentStreakLength++;
        day.streak = true;
      } else {
        break;
      }
    }
  }
  
  private calculateCurrentStreak(dailyProgress: DailyProgressData[]): number {
    if (dailyProgress.length === 0) return 0;
    
    let streak = 0;
    
    // Go backwards from most recent day
    for (let i = dailyProgress.length - 1; i >= 0; i--) {
      if (dailyProgress[i].sessionsCount > 0) {
        streak++;
      } else {
        break;
      }
    }
    
    return streak;
  }
  
  // Method removed as it was unused
  // private calculateLongestStreak(dailyProgress: DailyProgressData[]): number { ... }
  
  private getEmptyStats(): DashboardStats {
    return {
      overall: {
        timeSpent: 0,
        totalKeystrokes: 0,
        accuracy: 0,
        avgSpeed: 0,
        wpm: 0,
        bestWord5: 0,
        trend: 'stable',
        sessionsCount: 0,
        improvementPercent: 0
      },
      letters: [],
      dailyProgress: [],
      fingerPerformance: [],
      weakSpots: [],
      calendarData: [],
      achievements: []
    };
  }
}

// Export singleton instance
export const statisticsService = new StatisticsDataService();
